import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Plus, Pencil, Trash2, X, Car, User, FileText, Eye, Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import SearchableSelect from './SearchableSelect';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const EMPTY = {
  vehicle: '', country: 'ge', date_from: '', date_to: '',
  policy_number: '', vin: '', driver_name: '', driver_passport_number: '',
};

function authHeaders(json = true) {
  const token = localStorage.getItem('auth_token');
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function expiryBadge(dateTo) {
  if (!dateTo) return null;
  const days = Math.ceil((new Date(dateTo) - new Date()) / 86400000);
  if (days < 0)   return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Expired</span>;
  if (days <= 10) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Expires in {days}d</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
}

// Input styling matching the user-facing insurance modal (InsurancePage).
const userInp = 'w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed read-only:bg-gray-100';

const COUNTRY_FLAGS = {
  ge: 'inline-block w-6 h-4 bg-gradient-to-r from-red-600 to-white rounded',
  ru: 'inline-block w-6 h-4 bg-gradient-to-b from-white via-blue-500 to-red-600 rounded',
  am: 'inline-block w-6 h-4 bg-gradient-to-b from-red-600 via-blue-500 to-orange-500 rounded',
};

export default function AdminInsurance() {
  const { t } = useLanguage();
  const [rows, setRows]         = useState([]);
  const [users, setUsers]       = useState([]);
  const [selUser, setSelUser]   = useState('');
  const [country, setCountry]   = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [createUser, setCreateUser]       = useState('');
  const [createVehicles, setCreateVehicles] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [docsToDelete, setDocsToDelete]   = useState([]);
  const [takenCountries, setTakenCountries] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/users/`, { headers: authHeaders(false) });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  const fetchData = useCallback(async (uid, c) => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (uid) p.set('created_by', uid);
      if (c)   p.set('country', c);
      const res = await fetch(`${BASE_URL}/insurances/?${p}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(Array.isArray(data) ? data : (data.results || []));
      setCurrentPage(1);
    } catch { setError('Failed to load insurance records.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchData(selUser, country); }, [selUser, country, fetchData]);

  const loadAllVehicles = async () => {
    setCreateVehicles([]);
    try {
      const res = await fetch(`${BASE_URL}/vehicles/admin-vehicles/`, { headers: authHeaders(false) });
      if (res.ok) setCreateVehicles(await res.json());
    } catch {}
  };

  // Which countries already have insurance for the selected vehicle — used to
  // block creating a duplicate (one insurance per vehicle+country).
  const loadVehicleInsurances = async (vid) => {
    setTakenCountries([]);
    if (!vid) return;
    try {
      const res = await fetch(`${BASE_URL}/insurances/?vehicle_id=${vid}`, { headers: authHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.results || []);
        const taken = list.map(i => i.country);
        setTakenCountries(taken);
        // Auto-select the first country that is still free.
        const firstFree = ['ge', 'ru', 'am'].find(c => !taken.includes(c));
        if (firstFree) setForm(prev => ({ ...prev, country: firstFree }));
      }
    } catch {}
  };

  const resetFiles = () => { setAttachedFiles([]); setDocsToDelete([]); setTakenCountries([]); };
  const openCreate = () => {
    setForm(EMPTY);
    setCreateUser('');
    resetFiles();
    loadAllVehicles();
    setModal({ mode: 'create' });
  };
  const openEdit   = (r) => {
    setForm({ ...EMPTY, ...r, vehicle: r.vehicle || '' });
    resetFiles();
    setModal({ mode: 'edit', id: r.id, row: r });
  };
  const openView   = (r) => {
    setForm({ ...EMPTY, ...r, vehicle: r.vehicle || '' });
    resetFiles();
    setModal({ mode: 'view', id: r.id, row: r });
  };

  const handleSave = async () => {
    // Validation (mirrors the user-facing insurance modal).
    if (modal.mode === 'create' && !form.vehicle) {
      alert('Please select a vehicle.');
      return;
    }
    if (!form.date_from || !form.date_to) {
      alert('Please fill in both Date From and Date To.');
      return;
    }
    // Guard against creating a duplicate (vehicle already insured for this country).
    if (modal.mode === 'create' && takenCountries.includes(form.country)) {
      alert('This vehicle already has insurance for the selected country.');
      return;
    }
    setSaving(true);
    try {
      const isEdit = modal.mode === 'edit';
      const url    = isEdit ? `${BASE_URL}/insurances/${modal.id}/` : `${BASE_URL}/insurances/`;
      const res = await fetch(url, { method: isEdit ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      const insId = saved.id || modal.id;

      // Delete documents marked for removal (edit mode).
      for (const docId of docsToDelete) {
        try {
          await fetch(`${BASE_URL}/insurances/${insId}/delete_document/`, {
            method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ document_id: docId }),
          });
        } catch {}
      }
      // Upload newly attached files (FormData → no JSON Content-Type).
      for (const file of attachedFiles) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await fetch(`${BASE_URL}/insurances/${insId}/upload_document/`, {
            method: 'POST', headers: authHeaders(false), body: fd,
          });
        } catch {}
      }
      setModal(null);
      fetchData(selUser, country);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/insurances/${id}/`, { method: 'DELETE', headers: authHeaders(false) });
      if (!res.ok && res.status !== 204) throw new Error();
      setDeleteId(null);
      fetchData(selUser, country);
    } catch { alert('Failed to delete.'); }
  };

  const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const usernameById = (id) => users.find(u => String(u.id) === String(id))?.username || '';

  // Client-side search by plate number (and VIN), mirroring the user-facing insurance page.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        (r.plate_number || r.vehicle || '').toString().toLowerCase().includes(q) ||
        (r.vin || '').toString().toLowerCase().includes(q))
    : rows;

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('insurance')}</h1>
        <div className="flex gap-2">
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={15}/> Add
          </button>
          <button onClick={() => fetchData(selUser, country)} disabled={loading} className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('refresh')}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={selUser} onChange={e => setSelUser(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <select value={country} onChange={e => setCountry(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All countries</option>
          <option value="ge">{t('insuranceGE')}</option>
          <option value="ru">{t('insuranceRU')}</option>
          <option value="am">{t('insuranceAM')}</option>
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder={t('search')}
            className="w-full text-sm border border-gray-300 rounded-md pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setCurrentPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle size={16}/>{error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  'Vehicle',
                  'Country',
                  t('policyNumber'),
                  t('driverInfo'),
                  t('dateFrom'),
                  t('dateTo'),
                  'Status',
                  'Created by',
                  t('actions'),
                ].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? [...Array(6)].map((_, i) => (
                <tr key={i}>{[...Array(9)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>
              )) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400">No insurance records found</td></tr>
              ) : paged.map(r => (
                <tr key={r.id} className="hover:bg-primary-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{r.plate_number || r.vehicle}</td>
                  <td className="px-4 py-3 uppercase text-gray-600">{r.country}</td>
                  <td className="px-4 py-3 text-gray-600">{r.policy_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.driver_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.date_from}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.date_to}</td>
                  <td className="px-4 py-3">{expiryBadge(r.date_to)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.created_by || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openView(r)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="View"><Eye size={14}/></button>
                      <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-primary-50 text-primary-600" title="Edit"><Pencil size={14}/></button>
                      <button onClick={() => setDeleteId(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} / {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}{q ? ` (of ${rows.length})` : ''}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {modal.mode === 'create'
                  ? 'New Insurance'
                  : (modal.row?.plate_number || form.vehicle)}
              </h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600 transition-colors"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* User + Vehicle (create): pick either first, they stay in sync.
                  Select user → vehicle list narrows to their vehicles.
                  Select vehicle → user auto-fills from the vehicle's owner. */}
              {modal.mode === 'create' && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <SearchableSelect
                      icon={<User className="h-5 w-5 text-gray-400" />}
                      placeholder="— Select user —"
                      value={createUser}
                      options={[{ value: '', label: 'All users' }, ...users.map(u => ({ value: u.id, label: u.username }))]}
                      onChange={(uid) => { setCreateUser(uid); setForm(prev => ({ ...prev, vehicle: '', vin: '' })); setTakenCountries([]); }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle *</label>
                    <SearchableSelect
                      icon={<Car className="h-5 w-5 text-gray-400" />}
                      placeholder="— Select vehicle —"
                      value={form.vehicle}
                      options={createVehicles
                        .filter(v => !createUser || String(v.created_by_id) === String(createUser))
                        .map(v => ({ value: v.id, label: createUser ? v.plate_number : `${v.plate_number}${usernameById(v.created_by_id) ? ' — ' + usernameById(v.created_by_id) : ''}` }))}
                      onChange={(vid) => {
                        const veh = createVehicles.find(v => String(v.id) === String(vid));
                        setCreateUser(veh?.created_by_id || createUser);
                        setForm(prev => ({ ...prev, vehicle: vid, vin: veh?.vin || '' }));
                        loadVehicleInsurances(vid);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Country selector — flag buttons (like the user modal).
                  In create mode, countries that already have insurance for the
                  selected vehicle are disabled (no duplicates). */}
              <div className="mb-6">
                <div className="flex gap-3">
                  {['ge', 'ru', 'am'].map(c => {
                    const taken = modal.mode === 'create' && takenCountries.includes(c);
                    const locked = modal.mode !== 'create' || taken;
                    return (
                      <button key={c} type="button"
                        onClick={() => modal.mode === 'create' && !taken && setForm(prev => ({ ...prev, country: c }))}
                        disabled={locked}
                        title={taken ? 'Insurance already exists for this country' : ''}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                          form.country === c ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        } ${locked ? 'cursor-default' : ''} ${taken ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-center gap-2">
                          <span className={COUNTRY_FLAGS[c]}></span>
                          <span className="font-medium">{t(c === 'ge' ? 'insuranceGE' : c === 'ru' ? 'insuranceRU' : 'insuranceAM')}</span>
                        </div>
                        {taken && <div className="text-[10px] mt-0.5 text-gray-500">already exists</div>}
                      </button>
                    );
                  })}
                </div>
                {modal.mode === 'create' && takenCountries.includes(form.country) && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <AlertCircle size={15}/> This vehicle already has insurance for the selected country.
                  </div>
                )}
              </div>

              {/* Form fields (layout matches the user modal) */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('licensePlate')}</label>
                    <input type="text" readOnly
                      value={modal.mode === 'create'
                        ? (createVehicles.find(v => String(v.id) === String(form.vehicle))?.plate_number || '')
                        : (modal.row?.plate_number || form.vehicle)}
                      className={userInp} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                    <input type="text" value={form.vin} readOnly className={userInp} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('start')}</label>
                    <input type="date" value={form.date_from} onChange={f('date_from')} disabled={modal.mode === 'view'} className={userInp} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('end')}</label>
                    <input type="date" value={form.date_to} onChange={f('date_to')} disabled={modal.mode === 'view'} className={userInp} required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('policyNumber')}</label>
                  <input type="text" value={form.policy_number} onChange={f('policy_number')} disabled={modal.mode === 'view'} className={userInp} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('driverInfo')}</label>
                    <input type="text" value={form.driver_name} onChange={f('driver_name')} disabled={modal.mode === 'view'} className={userInp} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('driverPass')}</label>
                    <input type="text" value={form.driver_passport_number} onChange={f('driver_passport_number')} disabled={modal.mode === 'view'} className={userInp} />
                  </div>
                </div>
              </div>

              <div className="mt-6" />

              {/* Insurance documents */}
              <div className="mb-2">
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">Insurance Documents</h3>

                {/* Existing documents (edit/view) */}
                {modal.mode !== 'create' && (
                  (modal.row?.documents || []).filter(d => !docsToDelete.includes(d.id)).length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {(modal.row?.documents || []).filter(d => !docsToDelete.includes(d.id)).map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{(doc.file_name || '').split(/[\\/]/).pop()}</p>
                              <p className="text-xs text-gray-500">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <a href={doc.file} target="_blank" rel="noopener noreferrer"
                              className="px-3 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-600 rounded hover:bg-blue-600 hover:text-white transition-colors">
                              View
                            </a>
                            {modal.mode === 'edit' && (
                              <button type="button" onClick={() => setDocsToDelete(prev => [...prev, doc.id])}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded" title="Delete document">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    modal.mode === 'view' && <p className="text-sm text-gray-400 mb-3">No documents attached.</p>
                  )
                )}

                {/* Attach new files (create/edit) */}
                {modal.mode !== 'view' && (
                  <div>
                    <input type="file" multiple accept="image/*,.pdf"
                      onChange={e => setAttachedFiles(prev => [...prev, ...Array.from(e.target.files)])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    {attachedFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium text-gray-600">New files:</p>
                        {attachedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            <span className="truncate">{file.name}</span>
                            <button type="button" onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                              className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">{modal.mode === 'view' ? t('close') || 'Close' : t('cancel')}</button>
              {modal.mode !== 'view' && (
                <button onClick={handleSave}
                  disabled={saving || (modal.mode === 'create' && (!form.vehicle || takenCountries.includes(form.country)))}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving ? 'Saving…' : (modal.mode === 'create' ? t('save') : t('update'))}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-80">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <h3 className="font-semibold">{t('delete')}</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">Delete this insurance record? This cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50">{t('cancel') || 'Cancel'}</button>
                <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700">{t('delete')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
